import booleanPointInPolygon from "@turf/boolean-point-in-polygon";
import { point } from "@turf/helpers";
import { Aircraft, Hangar, HangarBay } from "../types";

export interface HeightValidationResult {
  valid: boolean;
  warnings: string[];
  bay?: HangarBay;
  fittingBays?: number;
  totalBays?: number;
}

/**
 * Find which bay contains a given position using point-in-polygon detection
 * @param x X coordinate in feet (relative to hangar origin)
 * @param y Y coordinate in feet (relative to hangar origin)
 * @param bays Array of bay definitions with polygons
 * @returns The bay containing the position, or null if outside all bays
 */
export function findBayForPosition(
  x: number,
  y: number,
  bays: HangarBay[]
): HangarBay | null {
  if (!bays || bays.length === 0) {
    return null;
  }

  const testPoint = point([x, y]);

  for (const bay of bays) {
    if (booleanPointInPolygon(testPoint, bay.geom)) {
      return bay;
    }
  }

  return null;
}

/**
 * Validate if aircraft fits based on clear_height
 * Uses same logic as existing aircraftWarnings.ts:
 * - tail_height >= clear_height: ERROR (won't fit)
 * - tail_height + 1 >= clear_height: WARNING (tight fit)
 * - Otherwise: OK
 */
function checkHeightClearance(
  tailHeight: number,
  clearHeight: number
): { valid: boolean; warning?: string } {
  if (tailHeight >= clearHeight) {
    return {
      valid: false,
      warning: `Aircraft is ${tailHeight}ft tall but clearance is ${clearHeight}ft. This aircraft will not fit.`,
    };
  } else if (tailHeight + 1 >= clearHeight) {
    return {
      valid: true,
      warning: `Aircraft is ${tailHeight}ft tall and clearance is ${clearHeight}ft. Tow with extra caution.`,
    };
  }
  return { valid: true };
}

/**
 * Main validation function for aircraft height
 * @param aircraft Aircraft with tail_height
 * @param hangar Hangar with optional bays
 * @param position Optional position for bay-specific validation
 * @returns Validation result with warnings
 */
export function validateAircraftHeight(
  aircraft: Aircraft,
  hangar: Hangar,
  position?: { x: number; y: number }
): HeightValidationResult {
  // If hangar has no bays, use legacy validation
  if (!hangar.bays || hangar.bays.length === 0) {
    const check = checkHeightClearance(aircraft.tail_height, hangar.height);
    return {
      valid: check.valid,
      warnings: check.warning ? [check.warning] : [],
    };
  }

  // Hangar has bays
  if (position) {
    // Position-specific validation
    const bay = findBayForPosition(position.x, position.y, hangar.bays);

    if (bay) {
      // Aircraft is in a bay - check bay clearance
      const check = checkHeightClearance(
        aircraft.tail_height,
        bay.clear_height
      );
      return {
        valid: check.valid,
        warnings: check.warning ? [check.warning] : [],
        bay,
      };
    } else {
      // Position is outside all bays - use hangar height as fallback
      const check = checkHeightClearance(aircraft.tail_height, hangar.height);
      return {
        valid: check.valid,
        warnings: check.warning ? [check.warning] : [],
      };
    }
  } else {
    // No position specified - check if aircraft fits in ANY bay
    const fittingBayInfo: Array<{ index: number; height: number }> = [];
    const nonFittingBayInfo: Array<{ index: number; height: number }> = [];
    const totalBays = hangar.bays.length;

    hangar.bays.forEach((bay, index) => {
      if (aircraft.tail_height < bay.clear_height) {
        fittingBayInfo.push({ index: index + 1, height: bay.clear_height });
      } else {
        nonFittingBayInfo.push({ index: index + 1, height: bay.clear_height });
      }
    });

    const fittingBays = fittingBayInfo.length;

    // Build descriptive message with specific bay information
    let warning = "";

    if (fittingBays === 0) {
      // Fits in no bays
      const bayList = nonFittingBayInfo
        .map((bay) => `Bay ${bay.index} (${bay.height}ft)`)
        .join(", ");
      warning = `Does not fit in any bay. ${bayList}. Aircraft is ${aircraft.tail_height}ft tall.`;
      return {
        valid: false,
        warnings: [warning],
        fittingBays,
        totalBays,
      };
    } else if (fittingBays < totalBays) {
      // Fits in some bays
      const fittingList = fittingBayInfo
        .map((bay) => `Bay ${bay.index} (${bay.height}ft clear)`)
        .join(", ");
      const nonFittingList = nonFittingBayInfo
        .map((bay) => `Bay ${bay.index} (${bay.height}ft clear)`)
        .join(", ");
      warning = `Fits in ${fittingList} but NOT in ${nonFittingList}. Aircraft is ${aircraft.tail_height}ft tall.`;
      return {
        valid: true,
        warnings: [warning],
        fittingBays,
        totalBays,
      };
    } else {
      // Fits in all bays - no warning needed
      return {
        valid: true,
        warnings: [],
        fittingBays,
        totalBays,
      };
    }
  }
}
